// ═══════════════════════════════════════════════════════════════
// DiscreteProcessUI.jsx — verticalstack XC-DPUI shell component
// Path: src/components/DiscreteProcessUI.jsx
//
// S3.A (May 18, 2026) — NEW FILE. Ports the v7.2 sandbox
// renderDiscreteProcess() architectural pattern into a generic React
// component.
//
// S4.C (May 18, 2026) — Subtitle + breakdown text updated to match
// the S3.5 canonical registry: 30 processes (21 FULL + 9 VALID + 3
// demoted) per Taxonomy v11.1 PROCESS-INVENTORY. Closes AS-S3.5-010
// and AS-S3.5-009 (sandbox staleness inherited into the React file).
//
// PURPOSE
//   One component. Every discrete process (Business Case, Work Order,
//   Lease Renewal, Bid Package, RFI, Invoice, 1031 Exchange, and 23 more
//   per Taxonomy v11.1) renders through this same component, configured
//   by a config object from processConfigs.js.
//
//   "Build the component once. Configure 30 processes."
//
// SOURCE PATTERN
//   verticalstack_FullLifecycle_Demo_v7_2.html · renderDiscreteProcess()
//   · line 1832. Ported verbatim:
//     - 6-qualifier test banner (architecture-explanation block)
//     - Card with colored header (uses config.color · per-stage accent)
//     - Current instance row
//     - Status workflow strip (current status highlighted in config.color)
//     - Gate banner (amber background — distinctive across all processes)
//     - Streams active rows (API + AI)
//     - Record outcome footer
//     - "What stays identical vs. what changes" summary
//     - History tab static placeholder
//
// STATE
//   None. Pure presentation. Props in, JSX out. State, fetches, and
//   gate-fire wiring land in Tier 3 (S13–S23) per-process sessions.
//
// PROPS
//   config (required) — one entry from PROCESS_CONFIGS in processConfigs.js
//   instance (optional) — string override for current-instance label
//
// STYLING
//   Inline styles. Sandbox CSS variables resolved to literal hex.
//   Per-process accent comes from config.color, not from any brand palette.
//   Sandbox amber #FDF3E3 + #7A4A08 reserved for the gate banner across
//   all configurations (gates are visually distinctive — every process
//   uses the same amber treatment so the gate is recognizable anywhere).
// ═══════════════════════════════════════════════════════════════

import React from 'react';

// ── Inline style tokens ──────────────────────────────────────────────────

const styles = {
  // Outer container — matches Shell content area conventions
  container: {
    maxWidth: 1200,
    margin: '0 auto',
    fontFamily: 'Arial, sans-serif',
  },

  // Banner above the page title
  banner: {
    backgroundColor: '#F1EBFA',
    border: '1px solid rgba(110, 74, 160, 0.2)',
    borderRadius: 8,
    padding: '12px 18px',
    marginBottom: 20,
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    flexWrap: 'wrap',
    gap: 12,
  },
  bannerTitle: {
    fontSize: 13,
    fontWeight: 700,
    color: '#3B2566',
  },
  bannerTag: {
    display: 'inline-block',
    marginLeft: 8,
    padding: '2px 8px',
    fontSize: 10,
    fontWeight: 700,
    color: '#6E4AA0',
    background: 'rgba(110, 74, 160, 0.12)',
    borderRadius: 10,
    textTransform: 'uppercase',
    letterSpacing: 0.4,
  },
  bannerMuted: {
    fontSize: 12,
    color: '#6B7280',
    fontFamily: 'monospace',
  },

  // Page header
  pageHead: {
    marginBottom: 18,
  },
  pageTitle: {
    fontSize: 24,
    fontWeight: 700,
    color: '#003060',
    margin: 0,
    marginBottom: 4,
  },
  pageSub: {
    fontSize: 14,
    color: '#6B7280',
    margin: 0,
  },

  // 6-qualifier test block
  qualifierBlock: {
    background: '#F1EBFA',
    border: '1px solid rgba(110, 74, 160, 0.2)',
    borderRadius: 8,
    padding: '14px 18px',
    marginBottom: 20,
  },
  qualifierHeader: {
    fontSize: 13,
    fontWeight: 700,
    color: '#6E4AA0',
    marginBottom: 8,
  },
  qualifierGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
    gap: 6,
    fontSize: 12,
    color: '#3B2566',
  },
  qualifierPill: {
    padding: '6px 10px',
    background: 'rgba(110, 74, 160, 0.10)',
    borderRadius: 6,
  },
  qualifierFooter: {
    marginTop: 10,
    fontSize: 12,
    color: '#3B2566',
  },

  // Single-config card
  card: {
    background: '#FFFFFF',
    border: '1px solid #E5E7EB',
    borderRadius: 12,
    overflow: 'hidden',
    boxShadow: '0 1px 2px rgba(0, 0, 0, 0.04), 0 2px 8px rgba(0, 0, 0, 0.03)',
    marginBottom: 20,
  },
  cardHeader: {
    // backgroundColor injected per-config (config.color)
    padding: '14px 18px',
    display: 'flex',
    alignItems: 'center',
    gap: 12,
  },
  cardIcon: {
    fontSize: 24,
    color: '#FFFFFF',
  },
  cardLabel: {
    fontSize: 15,
    fontWeight: 700,
    color: '#FFFFFF',
  },
  cardStage: {
    fontSize: 11,
    color: 'rgba(255, 255, 255, 0.7)',
    marginTop: 2,
    fontFamily: 'monospace',
  },

  // Sections inside card
  section: {
    padding: '14px 18px',
    borderBottom: '1px solid #E5E7EB',
  },
  sectionLabel: {
    fontSize: 10,
    color: '#9CA3AF',
    textTransform: 'uppercase',
    letterSpacing: 0.5,
    marginBottom: 4,
    fontWeight: 600,
  },
  sectionValue: {
    fontSize: 13,
    color: '#1A1A1A',
    fontWeight: 600,
  },

  // Status strip
  statusStrip: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: 4,
    marginTop: 4,
  },
  statusPill: {
    fontSize: 11,
    fontWeight: 600,
    padding: '4px 10px',
    borderRadius: 12,
    background: '#F3F4F6',
    color: '#6B7280',
  },
  statusPillCurrent: {
    // backgroundColor injected per-config
    color: '#FFFFFF',
  },

  // Gate banner — amber across all configs
  gateBanner: {
    padding: '14px 18px',
    background: '#FDF3E3',
    borderBottom: '1px solid #E5E7EB',
  },
  gateHeader: {
    fontSize: 10,
    fontWeight: 700,
    color: '#7A4A08',
    textTransform: 'uppercase',
    letterSpacing: 0.5,
    marginBottom: 4,
  },
  gateLabel: {
    fontSize: 13,
    fontWeight: 700,
    color: '#7A4A08',
    marginBottom: 2,
  },
  gateDescription: {
    fontSize: 12,
    color: '#7A4A08',
    lineHeight: 1.5,
  },

  // Streams rows
  streamRow: {
    fontSize: 12,
    marginBottom: 4,
  },
  streamApi: {
    color: '#1A4F7A',
  },
  streamAi: {
    color: '#6E4AA0',
  },
  streamPrefix: {
    fontWeight: 700,
  },

  // Record outcome footer
  recordOutcome: {
    padding: '14px 18px',
  },
  recordValue: {
    fontSize: 12,
    color: '#0A5C42',
  },

  // "What stays identical vs. changes" two-column block
  comparisonCard: {
    background: '#FFFFFF',
    border: '1px solid #E5E7EB',
    borderRadius: 12,
    padding: '20px 22px',
    marginBottom: 20,
  },
  comparisonTitle: {
    fontSize: 14,
    fontWeight: 700,
    color: '#003060',
    marginBottom: 14,
  },
  comparisonGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
    gap: 24,
  },
  comparisonColHeader: {
    fontSize: 12,
    fontWeight: 700,
    marginBottom: 8,
  },
  comparisonColIdentical: {
    color: '#0A5C42',
  },
  comparisonColConfigured: {
    color: '#6E4AA0',
  },
  comparisonItem: {
    fontSize: 12,
    color: '#374151',
    padding: '6px 0',
    borderBottom: '1px solid #F3F4F6',
  },

  // History tab placeholder
  historyCard: {
    background: '#F8F9FA',
    border: '1px dashed #D1D5DB',
    borderRadius: 8,
    padding: '14px 18px',
    marginBottom: 20,
  },
  historyHeader: {
    fontSize: 11,
    fontWeight: 700,
    color: '#6B7280',
    textTransform: 'uppercase',
    letterSpacing: 0.5,
    marginBottom: 6,
  },
  historyBody: {
    fontSize: 12,
    color: '#6B7280',
    fontStyle: 'italic',
  },

  // Smoke footer
  smokeFooter: {
    marginTop: 24,
    padding: 16,
    backgroundColor: '#FEF3C7',
    border: '1px solid #FBBF24',
    borderRadius: 6,
    fontSize: 12,
    color: '#92400E',
    lineHeight: 1.5,
  },
};

// ── 6-qualifier test data (architectural reference, not config) ──────────
// These are the universal qualifiers from v7.2 sandbox line 1881.
// Same six show on every DPUI render — they describe what makes a process
// a "discrete process" in the verticalstack architecture.
const SIX_QUALIFIERS = [
  '① Container created',
  '② Status workflow (3–6 statuses)',
  '③ Gate that fires',
  '④ Record created/updated at gate',
  '⑤ API / data pull triggered',
  '⑥ AI conversion triggered',
];

const IDENTICAL_LIST = [
  'Pipeline card view (container list with status)',
  'Status strip (visual funnel — current state highlighted)',
  'Gate action button (fires the transition)',
  'History tab (all prior statuses preserved, never deleted)',
  'Document attachment (AI-08 OCR on every upload)',
  'AI pill indicators on processed items',
  'Notes / comments field',
];

const CONFIGURED_LIST = [
  'Status labels (Active/New/Draft/Issued etc.)',
  'Gate label and condition text',
  'Destination record type created at gate',
  'API connections triggered at each status',
  'AI functions called at each status',
  'Card metadata fields displayed',
  'Color accent (matches parent module)',
];

// ── Main component ───────────────────────────────────────────────────────

export default function DiscreteProcessUI({ config, instance }) {
  // Defensive — if no config passed (or registry lookup returned null),
  // render a clear error rather than crashing on undefined access.
  if (!config) {
    return (
      <div style={styles.container}>
        <div style={styles.smokeFooter}>
          <strong>DiscreteProcessUI:</strong> no config provided. Pass a config
          object from PROCESS_CONFIGS in processConfigs.js.
        </div>
      </div>
    );
  }

  const displayInstance = instance || config.instance || '—';

  return (
    <div style={styles.container}>
      {/* ── Architectural banner ──────────────────────────────────────── */}
      <div style={styles.banner}>
        <div>
          <span style={styles.bannerTitle}>Discrete Process Component</span>
          <span style={styles.bannerTag}>Platform-Level Architecture</span>
        </div>
        <div style={styles.bannerMuted}>
          One component · 30 configurations · built once · wired to context
        </div>
      </div>

      {/* ── Page header ──────────────────────────────────────────────── */}
      <header style={styles.pageHead}>
        <h1 style={styles.pageTitle}>Discrete Process UI Component</h1>
        <p style={styles.pageSub}>
          Same container + status strip + gate action + history tab across
          all 30 processes — rendering one configuration here as the smoke
          reference.
        </p>
      </header>

      {/* ── 6-qualifier test ─────────────────────────────────────────── */}
      <div style={styles.qualifierBlock}>
        <div style={styles.qualifierHeader}>
          The 6-qualifier test — all must pass to qualify as a discrete process
        </div>
        <div style={styles.qualifierGrid}>
          {SIX_QUALIFIERS.map((q) => (
            <div key={q} style={styles.qualifierPill}>{q}</div>
          ))}
        </div>
        <div style={styles.qualifierFooter}>
          <strong>30 confirmed processes</strong> · 21 FULL (all 6 qualifiers)
          · 9 VALID (1–4 + AI, no external API) · 3 demoted to field/report/
          transaction · Missing 1–4 = not a process
        </div>
      </div>

      {/* ── Single-config card ───────────────────────────────────────── */}
      <div style={styles.card}>
        {/* Header — color from config */}
        <div style={{ ...styles.cardHeader, backgroundColor: config.color }}>
          <span style={styles.cardIcon}>{config.icon}</span>
          <div>
            <div style={styles.cardLabel}>{config.label}</div>
            <div style={styles.cardStage}>{config.stage}</div>
          </div>
        </div>

        {/* Current instance */}
        <div style={{ ...styles.section, background: '#F8F9FA' }}>
          <div style={styles.sectionLabel}>Current instance</div>
          <div style={styles.sectionValue}>{displayInstance}</div>
        </div>

        {/* Status workflow strip */}
        <div style={styles.section}>
          <div style={styles.sectionLabel}>Status workflow</div>
          <div style={styles.statusStrip}>
            {config.statuses.map((s) => {
              const isCurrent = s === config.currentStatus;
              return (
                <span
                  key={s}
                  style={{
                    ...styles.statusPill,
                    ...(isCurrent
                      ? { ...styles.statusPillCurrent, background: config.color }
                      : {}),
                  }}
                >
                  {s}
                </span>
              );
            })}
          </div>
        </div>

        {/* Gate banner — amber across all configs */}
        <div style={styles.gateBanner}>
          <div style={styles.gateHeader}>⚑ Gate</div>
          <div style={styles.gateLabel}>{config.gateLabel} fires</div>
          <div style={styles.gateDescription}>{config.gateDescription}</div>
        </div>

        {/* Streams active */}
        <div style={styles.section}>
          <div style={styles.sectionLabel}>Streams active</div>
          <div style={{ ...styles.streamRow, ...styles.streamApi }}>
            <span style={styles.streamPrefix}>API: </span>
            {config.apiStreams}
          </div>
          <div style={{ ...styles.streamRow, ...styles.streamAi }}>
            <span style={styles.streamPrefix}>AI: </span>
            {config.aiStreams}
          </div>
        </div>

        {/* Record outcome */}
        <div style={styles.recordOutcome}>
          <div style={styles.sectionLabel}>Record outcome at gate</div>
          <div style={styles.recordValue}>{config.recordOutcome}</div>
        </div>
      </div>

      {/* ── History tab static placeholder ───────────────────────────── */}
      <div style={styles.historyCard}>
        <div style={styles.historyHeader}>History</div>
        <div style={styles.historyBody}>
          History tab placeholder. Per-process history wiring lands in
          Tier 3 (S13–S23) — each discrete process activation will
          populate this tab from its own audit collection.
        </div>
      </div>

      {/* ── What stays identical vs. configured ──────────────────────── */}
      <div style={styles.comparisonCard}>
        <div style={styles.comparisonTitle}>
          What stays identical vs. what changes per configuration
        </div>
        <div style={styles.comparisonGrid}>
          <div>
            <div style={{ ...styles.comparisonColHeader, ...styles.comparisonColIdentical }}>
              ✓ Identical across all 30 processes
            </div>
            {IDENTICAL_LIST.map((i) => (
              <div key={i} style={styles.comparisonItem}>• {i}</div>
            ))}
          </div>
          <div>
            <div style={{ ...styles.comparisonColHeader, ...styles.comparisonColConfigured }}>
              ↺ Configured per process type
            </div>
            {CONFIGURED_LIST.map((i) => (
              <div key={i} style={styles.comparisonItem}>• {i}</div>
            ))}
          </div>
        </div>
      </div>

      {/* ── S3 smoke footer ──────────────────────────────────────────── */}
      <div style={styles.smokeFooter}>
        <strong>S3 smoke render.</strong> XC-DPUI shell component live. Pure
        presentation — gate button is visual only, no Firestore reads or
        writes this session. Tier 3 sessions (S13–S23) wire per-process
        state, history, and gate-fire behavior.
      </div>
    </div>
  );
}
